import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TestTube, Calendar, FileText, Phone, Mail, Eye } from "lucide-react";
import { format } from "date-fns";

const statusConfig = {
  pending: {
    label: "Pending",
    variant: "secondary",
    color: "bg-yellow-100 text-yellow-800",
  },
  in_progress: {
    label: "In Progress",
    variant: "default",
    color: "bg-blue-100 text-blue-800",
  },
  completed: {
    label: "Completed",
    variant: "success",
    color: "bg-green-100 text-green-800",
  },
  cancelled: {
    label: "Cancelled",
    variant: "destructive",
    color: "bg-red-100 text-red-800",
  },
};

export default function PatientRequestsView({ requests, isLoading, onView }) {
  if (isLoading) {
    return (
      <div className="grid gap-6">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="text-center py-12">
        <TestTube className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Test Requests
        </h3>
        <p className="text-gray-500 mb-6">No test requests yet.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      {requests.map((request) => (
        <Card key={request.id} className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TestTube className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {request.requestedTests?.join(", ") || "Test Request"}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Request #{request.id?.slice(-8) || "N/A"}
                  </p>
                </div>
              </div>
              <Badge
                className={
                  statusConfig[request.status]?.color ||
                  "bg-gray-100 text-gray-800"
                }
              >
                {statusConfig[request.status]?.label || request.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {request.preferredDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Preferred Date:</span>
                    <span className="font-medium">
                      {format(new Date(request.preferredDate), "MMM d, yyyy")}
                    </span>
                  </div>
                )}

                {request.preferredTime && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Preferred Time:</span>
                    <span className="font-medium">{request.preferredTime}</span>
                  </div>
                )}

                {request.createdAt && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Requested:</span>
                    <span className="font-medium">
                      {format(new Date(request.createdAt), "MMM d, yyyy")}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {request.contactMethod && (
                  <div className="flex items-center gap-2 text-sm">
                    {request.contactMethod === "phone" ? (
                      <Phone className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Mail className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-gray-600">Contact via:</span>
                    <span className="font-medium capitalize">
                      {request.contactMethod}
                    </span>
                  </div>
                )}

                {request.urgency && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Urgency:</span>
                    <Badge
                      variant={
                        request.urgency === "high" ? "destructive" : "secondary"
                      }
                    >
                      {request.urgency}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {request.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Notes:
                    </p>
                    <p className="text-sm text-gray-600">{request.notes}</p>
                  </div>
                </div>
              </div>
            )}

            {request.followUpInstructions && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-1">
                  Follow-up Instructions:
                </p>
                <p className="text-sm text-blue-700">
                  {request.followUpInstructions}
                </p>
              </div>
            )}

            {/* View Details Button */}
            <div className="mt-4 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView?.(request)}
                className="w-full sm:w-auto"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
